import { garmentNoun, inkVariantFor, type DesignInk } from "./merchDesigns";
import { buildMockupUrl, ITEM_MOCKUP_WIDTH } from "./merchMockups";
import { MERCH_CATEGORIES, type MerchCategory } from "./merchPricing";

export interface StoreItemColor {
  name: string;
  label: string;
  hex: string;
  isDark: boolean;
  blankPublicId: string | null;
}

export interface StoreItemInk {
  key: string;
  ink: DesignInk;
  cloudinaryPublicId: string | null;
}

export interface StoreListItem {
  designSlug: string;
  designKey: string;
  designTitle: string;
  releaseName: string | null;
  releasePath: string | null;
  spotifyAlbumId: string | null;
  sku: string;
  productSlug: string;
  productName: string;
  category: MerchCategory;
  priceCents: number;
  currency: string;
  defaultColor: string;
  mockupUrl: string | null;
  colors: StoreItemColor[];
  inks: StoreItemInk[];
  featured: boolean;
  wordmark: boolean;
  sortOrder: number;
  createdAt: number;
}

export type GarmentFilter = "all" | MerchCategory;

export const ART_ALL = "all";

export const ART_LOGO = "logo";

export type SortKey = "featured" | "newest" | "price-asc" | "price-desc";

export interface StoreFilters {
  garment: GarmentFilter;
  art: string;
  sort: SortKey;
}

export const DEFAULT_STORE_FILTERS: StoreFilters = { garment: "all", art: ART_ALL, sort: "featured" };

export const GARMENT_FILTERS: Array<{ value: GarmentFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "tee", label: "Tees" },
  { value: "hoodie", label: "Hoodies" },
  { value: "sweatshirt", label: "Sweatshirts" },
  { value: "tank", label: "Tanks" },
];

export const SORT_OPTIONS: Array<{ value: SortKey; label: string }> = [
  { value: "featured", label: "Featured" },
  { value: "newest", label: "Newest" },
  { value: "price-asc", label: "Price, low to high" },
  { value: "price-desc", label: "Price, high to low" },
];

const SORT_KEYS = new Set<string>(SORT_OPTIONS.map((option) => option.value));

const SLUG_PATTERN = /^[a-z0-9-]{1,120}$/;

export function isGarmentFilter(value: string | null | undefined): value is GarmentFilter {
  return value === "all" || (MERCH_CATEGORIES as readonly string[]).includes(String(value));
}

export function parseStoreFilters(search: string | URLSearchParams): StoreFilters {
  const params = typeof search === "string" ? new URLSearchParams(search) : search;
  const garment = (params.get("garment") ?? "").trim().toLowerCase();
  const art = (params.get("art") ?? "").trim().toLowerCase();
  const sort = (params.get("sort") ?? "").trim().toLowerCase();
  return {
    garment: isGarmentFilter(garment) ? garment : DEFAULT_STORE_FILTERS.garment,
    art: art === ART_LOGO || SLUG_PATTERN.test(art) ? art : DEFAULT_STORE_FILTERS.art,
    sort: SORT_KEYS.has(sort) ? (sort as SortKey) : DEFAULT_STORE_FILTERS.sort,
  };
}

export function storeFiltersToSearch(filters: StoreFilters, current?: URLSearchParams | string): URLSearchParams {
  const params = new URLSearchParams(current ?? "");
  const write = (key: keyof StoreFilters) => {
    if (filters[key] === DEFAULT_STORE_FILTERS[key]) params.delete(key);
    else params.set(key, filters[key]);
  };
  write("garment");
  write("art");
  write("sort");
  return params;
}

export function activeStoreFilterCount(filters: StoreFilters): number {
  let active = 0;
  if (filters.garment !== DEFAULT_STORE_FILTERS.garment) active += 1;
  if (filters.art !== DEFAULT_STORE_FILTERS.art) active += 1;
  if (filters.sort !== DEFAULT_STORE_FILTERS.sort) active += 1;
  return active;
}

export function hasActiveStoreFilters(filters: StoreFilters): boolean {
  return activeStoreFilterCount(filters) > 0;
}

/**
 * How many cards the grid mounts at a time. Every card that is not yet on a
 * revealed page renders nothing at all, so it asks Cloudinary for nothing.
 */
export const STORE_PAGE_SIZE = 24;

export function storePageSlice<T>(items: T[], pages: number): T[] {
  return items.slice(0, Math.max(1, Math.floor(pages)) * STORE_PAGE_SIZE);
}

export function hasMoreStorePages(total: number, pages: number): boolean {
  return total > Math.max(1, Math.floor(pages)) * STORE_PAGE_SIZE;
}

export const storeCardKey = (item: Pick<StoreListItem, "designSlug" | "sku">): string => `${item.designSlug}:${item.sku}`;

export function pendingStorePage<T>(items: T[], pages: number): T[] {
  const shown = storePageSlice(items, pages).length;
  return items.slice(shown, shown + STORE_PAGE_SIZE);
}

export function isLogoItem(item: Pick<StoreListItem, "releaseName">): boolean {
  return item.releaseName === null;
}

export interface ArtFilterOption {
  value: string;
  label: string;
  count: number;
}

export function artFilterOptions(items: StoreListItem[]): ArtFilterOption[] {
  const logoCount = items.filter(isLogoItem).length;
  const options: ArtFilterOption[] = [{ value: ART_ALL, label: "All artwork", count: items.length }];
  if (logoCount > 0) options.push({ value: ART_LOGO, label: "Logo", count: logoCount });
  const byDesign = new Map<string, ArtFilterOption>();
  for (const item of items) {
    if (isLogoItem(item)) continue;
    const seen = byDesign.get(item.designSlug);
    if (seen) seen.count += 1;
    else byDesign.set(item.designSlug, { value: item.designSlug, label: item.designTitle, count: 1 });
  }
  for (const option of byDesign.values()) options.push(option);
  return options;
}

export function matchesStoreFilters(item: StoreListItem, filters: StoreFilters): boolean {
  if (filters.garment !== "all" && item.category !== filters.garment) return false;
  if (filters.art === ART_LOGO) return isLogoItem(item);
  if (filters.art !== ART_ALL) return item.designSlug === filters.art;
  return true;
}

function compareBySort(sort: SortKey) {
  return (a: StoreListItem, b: StoreListItem): number => {
    switch (sort) {
      case "newest":
        return b.createdAt - a.createdAt || a.sortOrder - b.sortOrder;
      case "price-asc":
        return a.priceCents - b.priceCents || a.sortOrder - b.sortOrder;
      case "price-desc":
        return b.priceCents - a.priceCents || a.sortOrder - b.sortOrder;
      default:
        return a.sortOrder - b.sortOrder;
    }
  };
}

export function applyStoreFilters(items: StoreListItem[], filters: StoreFilters): StoreListItem[] {
  return items.filter((item) => matchesStoreFilters(item, filters)).sort(compareBySort(filters.sort));
}

export function featuredItems(items: StoreListItem[], limit = 4): StoreListItem[] {
  const seen = new Set<string>();
  const families = new Set<string>();
  const picked: StoreListItem[] = [];
  const ordered = [...items].sort((a, b) => Number(b.wordmark) - Number(a.wordmark) || Number(b.featured) - Number(a.featured) || a.sortOrder - b.sortOrder);
  for (const item of ordered) {
    const family = item.designTitle.trim().toLowerCase();
    if (seen.has(item.designSlug) || families.has(family)) continue;
    seen.add(item.designSlug);
    families.add(family);
    picked.push(item);
    if (picked.length >= limit) break;
  }
  return picked;
}

export function garmentSupport(items: StoreListItem[]): Map<string, MerchCategory[]> {
  const support = new Map<string, MerchCategory[]>();
  for (const item of items) {
    const list = support.get(item.designSlug) ?? [];
    if (!list.includes(item.category)) list.push(item.category);
    support.set(item.designSlug, list);
  }
  return support;
}

export function alsoAvailableLine(categories: MerchCategory[] | undefined, current: MerchCategory): string | null {
  const others = (categories ?? []).filter((category) => category !== current);
  if (others.length === 0) return null;
  return `Also as ${others.map(garmentNoun).join(", ")}`;
}

export function availableAsLine(categories: MerchCategory[]): string {
  const nouns = [...new Set(categories.map(garmentNoun))];
  if (nouns.length === 0) return "";
  if (nouns.length === 1) return `Available as a ${nouns[0]}`;
  return `Available as ${nouns.slice(0, -1).join(", ")} and ${nouns[nouns.length - 1]}`;
}

export function itemName(item: Pick<StoreListItem, "designTitle" | "category">): string {
  return `${item.designTitle} ${garmentNoun(item.category)}`;
}

export function itemPath(item: Pick<StoreListItem, "designSlug" | "productSlug">, color?: string | null): string {
  const params = new URLSearchParams();
  params.set("garment", item.productSlug);
  if (color) params.set("color", color);
  return `/merch/${item.designSlug}?${params.toString()}`;
}

export function itemMockupUrl(item: StoreListItem, cloudName: string, width = ITEM_MOCKUP_WIDTH, colorName?: string | null): string | null {
  const color = item.colors.find((entry) => entry.name === (colorName ?? item.defaultColor)) ?? null;
  if (!cloudName || !color?.blankPublicId) return null;
  const ink = inkVariantFor(item.inks, color.isDark);
  if (!ink?.cloudinaryPublicId) return null;
  return buildMockupUrl({
    cloudName,
    blankPublicId: color.blankPublicId,
    designPublicId: ink.cloudinaryPublicId,
    category: item.category,
    garmentIsDark: color.isDark,
    width,
  });
}

export function pieceCountLabel(count: number): string {
  return `${count} ${count === 1 ? "piece" : "pieces"}`;
}

export function colorCountLabel(count: number): string {
  return `${count} ${count === 1 ? "colour" : "colours"}`;
}
